import React, { useEffect, useMemo, useState } from "react";
import { Paper, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SearchParams } from "../../core/models/consumptionsBillsSearchBar/consumptionsBillsSearchBar.model";
import DatePickersComponent from "../UI/DatePickersComponent/DatePickersComponent";
import ButtonElement from "../UI/ButtonElement/ButtonElement";
import { OwnerDataCups } from "../../core/models/ownerData/ownerData.model";
import { GetGraphDataRequestBody } from "../../core/models/consumptions/consumptions.model";
import {
  addDayDate,
  formatDateString,
} from "../../utils/dateUtils/formatDate.util";
import "./ConsumptionsBillsSearchBar.scss";
import { initialOwnerDataCup } from "../../utils/ownerData/ownerData.util";
import ConsumptionsManagementsDeliveryAddressComponent from "../ConsumptionsManagementsDeliveryAddressComponent/ConsumptionsManagementsDeliveryAddressComponent";
import { GroupedSuppliesTableRow } from "../../core/models/supplies/supplies.model";
import { useDatePickerValidation } from "../../utils/components/datePickers/datePickers.utils";
import dayjs from "dayjs";
import { ChangeEventDatePicker } from "../../core/models/bills/bills.model";
import {
  CustomDateError,
  DatePickerComponentTotalState,
} from "../../core/models/datePickersComponent/datePickersComponent.model";

interface ConsumptionsBillsSearchBarProps
  extends DatePickerComponentTotalState {
  currentCup?: GroupedSuppliesTableRow | null;
  ownerDataCups: OwnerDataCups[];
  handleClickSearch: (requestBody: GetGraphDataRequestBody) => void;
  searchParams: SearchParams;
  setSearchParams: React.Dispatch<React.SetStateAction<SearchParams>>;
  isLoading: boolean;
  isHourlySearch?: boolean;
  isFirstChartLoadFlag?: boolean;
  isChartLoad?: boolean;
  maxDateTo?: number;
  customDateError?: CustomDateError[];
}

const ConsumptionsBillsSearchBar = ({
  currentCup,
  ownerDataCups,
  handleClickSearch,
  searchParams,
  setSearchParams,
  isLoading,
  isHourlySearch = false,
  isChartLoad = false,
  isFirstChartLoadFlag,
  customDateError,
  maxDateTo,
  totalOptionIsChecked,
  setTotalOptionIsChecked,
}: ConsumptionsBillsSearchBarProps) => {
  const { t } = useTranslation();
  const [searchParamsSearchBar, setSearchParamsSearchBar] =
    useState<SearchParams>({
      dateFrom: null,
      dateTo: null,
      ownerDataCup: initialOwnerDataCup,
    });

  const { error, setError, isValidDate } = useDatePickerValidation();

  let isTextButtonFirstLoad: boolean;
  if (isChartLoad) {
    isTextButtonFirstLoad = isFirstChartLoadFlag ?? true;
  } else {
    isTextButtonFirstLoad = Object.values(searchParams).some(
      (searchParam) => !searchParam
    );
  }

  useEffect(() => {
    setSearchParamsSearchBar(searchParams);
  }, [searchParams]);

  const hasCodeCups = !!searchParamsSearchBar.ownerDataCup?.codeCups.length;
  const isButtonDisabled = useMemo(() => {
    const cupsIsSelected = hasCodeCups || currentCup;
    if (totalOptionIsChecked && cupsIsSelected) {
      return false;
    } else {
      return (
        !cupsIsSelected ||
        !searchParamsSearchBar.dateFrom ||
        !searchParamsSearchBar.dateTo ||
        isLoading ||
        !isValidDate
      );
    }
  }, [
    currentCup,
    hasCodeCups,
    isLoading,
    isValidDate,
    searchParamsSearchBar.dateFrom,
    searchParamsSearchBar.dateTo,
    totalOptionIsChecked,
  ]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const {
      dateFrom,
      dateTo,
      ownerDataCup: { idCups, codeCups },
    } = searchParamsSearchBar;

    const requestBody: GetGraphDataRequestBody = {
      dateFrom: totalOptionIsChecked
        ? ""
        : formatDateString(addDayDate(dateFrom?.$d as Date)).replace(",", ""),
      dateTo: totalOptionIsChecked
        ? ""
        : formatDateString(addDayDate(dateTo?.$d as Date)).replace(",", ""),
      cupsId: currentCup ? currentCup.cupsId : idCups,
      hourly: isHourlySearch,
      cupsNumber: isHourlySearch ? codeCups : undefined,
    };

    setSearchParams(searchParamsSearchBar);
    handleClickSearch(requestBody);
  };

  const formatCurrentCup = ({
    cif,
    town,
    socialReason,
    suppliesDirection,
    CUPS,
    cupsId,
    mainContract,
  }: GroupedSuppliesTableRow): OwnerDataCups => ({
    codeCups: CUPS,
    idCups: cupsId,
    idContract: mainContract.IdContrato.toString(),
    cif,
    envirorment: mainContract.Entorno.toLowerCase(),
    fee: mainContract.Tarifa,
    address: suppliesDirection,
    town,
    contractCode: mainContract.CodigoContrato.toString(),
    socialReason,
    date: mainContract.FechaContrato,
    situation: mainContract.ContratoSituacion,
  });

  const maxDate = searchParamsSearchBar.dateFrom
    ? ({
        $d: dayjs(searchParamsSearchBar.dateFrom.$d)
          .add(maxDateTo ?? 0, "month")
          .toDate(),
      } as ChangeEventDatePicker)
    : null;

  return (
    <Paper sx={{ width: "100%", borderRadius: "12px" }}>
      <Stack padding={{ xs: "1rem", md: "2rem" }}>
        <form
          onSubmit={(event) => handleSubmit(event)}
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: "1.5rem",
            width: "100%",
            paddingBottom: "12px",
          }}
          className="form-stack"
        >
          <Stack width={{ xs: "100%", md: "30%" }}>
            <ConsumptionsManagementsDeliveryAddressComponent
              currentCup={currentCup ? formatCurrentCup(currentCup) : undefined}
              ownerDataCups={ownerDataCups}
              ownerDataCupSelected={searchParamsSearchBar.ownerDataCup}
              setOwnerDataCupSelected={(ownerDataCupSelected: OwnerDataCups) =>
                setSearchParamsSearchBar((prevSearchParamsSearchBar) => ({
                  ...prevSearchParamsSearchBar,
                  ownerDataCup: ownerDataCupSelected,
                }))
              }
            />
          </Stack>

          <DatePickersComponent
            customDateError={customDateError ? customDateError : undefined}
            maxDateTo={maxDateTo ? maxDate : undefined}
            billsSearchBar
            error={error}
            setError={setError}
            filters={searchParamsSearchBar}
            totalOptionIsChecked={totalOptionIsChecked}
            setTotalOptionIsChecked={setTotalOptionIsChecked}
            setFilters={(id, value) =>
              setSearchParamsSearchBar((prevSearchParamsSearchBar) => ({
                ...prevSearchParamsSearchBar,
                [id]: value,
              }))
            }
          />
          <Stack width={{ xs: "100%", md: "20%" }}>
            <ButtonElement disabled={isButtonDisabled}>
              {t(
                `Consumos.${
                  isTextButtonFirstLoad
                    ? "Mostrar gráfico"
                    : "Actualizar gráfico"
                }`
              )}
            </ButtonElement>
          </Stack>
        </form>
      </Stack>
    </Paper>
  );
};

export default ConsumptionsBillsSearchBar;
